import {
  ComponentType,
  DashboardState,
  EScreenType,
  ESdChangeReason,
  EUserAccessPart,
  IDisplayItemComponent,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import React from "react";
import { ScreenService } from "../../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { getSdInstance } from "../../../../shared/utility/designer-context";
import { duplicateDesignerScreen } from "../../../../shared/utility/designer-screen-actions";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaComponentIcon from "../../../ui-kit/kuika-component-icon/kuika-component-icon";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaMenuItem from "../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import "./right-panel-header.scss";

import KuikaText from "../../../ui-kit/typography/Text/Text";
interface IRightPanelHeaderState {
  isRenaming: boolean;
  renamingId: string;
  isRenameLoading: boolean;
  dropdownVisible: boolean;
  triggerScreenDeleteModal: boolean;
}

class RightPanelHeader extends React.Component<IWithSDProps & IWithContextProp, IRightPanelHeaderState> {
  constructor(props) {
    super(props);
    this.state = {
      isRenaming: false,
      renamingId: "",
      isRenameLoading: false,
      dropdownVisible: false,
      triggerScreenDeleteModal: false
    };
  }

  componentDidUpdate(prevProps: Readonly<IWithSDProps & IWithContextProp>): void {
    if (this.props.sd.selectedKuikaIds[0] !== prevProps.sd.selectedKuikaIds[0]) {
      this.setState({ isRenaming: false, renamingId: "" });
      if (prevProps.sd.renamingDisplayItemId?.toString() === this.state.renamingId) {
        this.props.sd.setRenamingDisplayItemId();
      }
    }
  }

  getComponentIconImgByIconName = () => {
    const di = this.props.sd.screenDesignDictionary[parseInt(this.props.sd.selectedKuikaIds[0], 10)];

    if (di) {
      const targetComponent: IDisplayItemComponent | undefined = di?.item;
      if (targetComponent !== undefined) {
        const targetComponentDefinition = this.props?.sd?.getComponentDefinition(targetComponent);
        if (targetComponentDefinition) {
          return (
            <KuikaTooltip
              content={
                targetComponentDefinition.name === "div"
                  ? "Screen"
                  : this.props.sd.selectedKuikaIds.length === 1 && Number(this.props.sd.selectedKuikaIds[0]) > 0
                    ? this.props.sd.selectedKuikaIds[0]
                    : ""
              }
            >
              {targetComponentDefinition.name === "div" ? (
                // The screen itself reads with the same page glyph as the left rail's Screens button,
                // not the "div" component's empty square.
                <KuikaSvg type="sidebar-icons" name="file" width={16} height={16} />
              ) : (
                <KuikaComponentIcon name={targetComponentDefinition.name} width={16} height={16} />
              )}
            </KuikaTooltip>
          );
        }
      }
    }
  };

  getSelectedDisplayItem = () => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    return selectedDisplayItem;
  };

  getDiFriendlyNameNameByKuikaId = (kuikaId: string): string => {
    let result = kuikaId;
    if (kuikaId && kuikaId.length > 0) {
      const di = this.props.sd.screenDesignDictionary[parseInt(kuikaId, 10)];

      if (di?.friendlyName && di.friendlyName.length > 0) {
        result = di.friendlyName;
      }
    }
    return result;
  };

  getItemName = (isDisplay: boolean) => {
    const sd = this.props.sd.getScreenDesign();
    const di = this.props.sd.screenDesignDictionary[parseInt(this.props.sd.selectedKuikaIds[0], 10)];
    const component = this.props.sd.project?.componentSetObj?.components?.find((a) => a.id === di?.item?.id);
    const isDeprecated = component?.isDeprecated;
    if (isDeprecated && isDisplay) {
      return `(Deprecated) ${this.getDiFriendlyNameNameByKuikaId(this.props.sd.selectedKuikaIds[0])}`;
    }
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI && sd.name) {
      return sd.name;
    }
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI) {
      return this.props?.sd?.sdName;
    }
    return this.getDiFriendlyNameNameByKuikaId(this.props.sd.selectedKuikaIds[0]);
  };

  isScreenSelectedForRename = (): boolean => {
    return this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI && this.state.renamingId === this.props.sd.sdId;
  };

  isDisplayItemSelectedForRename = (): boolean => {
    return this.getSelectedDisplayItem()?.id.toString() === this.state.renamingId;
  };

  isItemRenamable = () => {
    const di = this.props.sd.screenDesignDictionary[parseInt(this.props.sd.selectedKuikaIds[0], 10)];
    const isLayoutComponent = this.isLayoutComponent(di);
    if (isLayoutComponent) {
      return false;
    }
    if (di && di.item) {
      const diItem = di.item;
      const compDef = this.props.sd.getComponentDefinition(diItem);
      if (compDef?.isRenamingDisabled === true) return false;
    }
    return true;
  };

  isLayoutComponent = (node: any): boolean => {
    const item: IDisplayItemComponent | undefined = node?.item;
    const itemDefinition = this.props.sd.getComponentDefinition(item);
    if (
      itemDefinition &&
      (itemDefinition.componentType === ComponentType.layoutLeft ||
        itemDefinition.componentType === ComponentType.layoutRight ||
        itemDefinition.componentType === ComponentType.layoutHeader ||
        itemDefinition.componentType === ComponentType.layoutFooter)
    ) {
      return true;
    }
    return false;
  };

  renameSelectedItem = () => {
    let renamingId = "";
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI) {
      renamingId = this.props.sd.sdId;
    } else {
      renamingId = this.props.sd.selectedKuikaIds[0];
      this.props.sd.setRenamingDisplayItemId(parseInt(renamingId, 10));
    }
    this.setState({ isRenaming: true, renamingId });
  };

  handleNameKeypress = async (e) => {
    try {
      if (e.keyCode === 13) {
        this.handleNameOnBlur(e);
      } else if (e.keyCode === 27) {
        this.setState({ isRenaming: false, renamingId: "" });
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  changeDiFriendlyName = (newName: string) => {
    if (newName && newName.length > 0) {
      const sd = this.props.sd.getScreenDesign();
      const di = this.props.sd.screenDesignDictionary[parseInt(this.props.sd.selectedKuikaIds[0], 10)];
      if (di) {
        di.friendlyName = newName;
        this.setState({ isRenaming: false, renamingId: "" }, () => {
          this.props.sd.setScreenDesign(sd, ESdChangeReason.ChangeDiFriendlyName);
        });
      }
    }
  };

  changeScreenName = async (newName: string) => {
    this.setState({ isRenameLoading: true });
    const screenNameRules = /^[A-Za-z][A-Za-z0-9 ]+$/;
    const isScreenNameValid = screenNameRules.test(newName);
    const isEqualToDsName = this.props.sd?.datasources?.find((ds) => ds.name.toLowerCase() === newName.toLowerCase());
    const isEqualToWfName = this.props.sd?.workFlowList?.find((wf) => wf.name.toLowerCase() === newName.toLowerCase());
    const isEqualToScreenName = this.props.sd?.screenList?.find(
      (s) => s.name.toLowerCase() === newName.toLowerCase() && s.id !== this.props.sd?.sdId
    );
    const isEqualToTableName = Array.from(this.props.sd?.schemas?.values() || []).some((schema) =>
      schema?.tables?.some((table) => table.name.toLowerCase() === newName.toLowerCase())
    );
    if (!isScreenNameValid) {
      KMainFunctions.displayWarningNotification("Screen Name is invalid");
      newName = this.getItemName(false);
      this.setState({ isRenameLoading: false });
      return;
    }
    if (isEqualToDsName || isEqualToWfName || isEqualToTableName) {
      KMainFunctions.displayWarningNotification(
        "Screen name can not be equal to the datasource, table or process name."
      );
      this.setState({ isRenameLoading: false });
      return;
    }
    if (isEqualToScreenName) {
      KMainFunctions.displayWarningNotification("Screen name already exists");
      this.setState({ isRenameLoading: false });
      return;
    }
    try {
      if (newName?.length > 0) {
        await this.props.sd.saveScreen(false, newName);
        this.props?.sd?.setIsScreenWindowReload(true);
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    } finally {
      this.setState({
        isRenaming: false,
        renamingId: "",
        isRenameLoading: false
      });
    }
  };

  handleNameOnBlur = async (e) => {
    let newName: any = e.target?.value ?? e.state.value;
    newName = newName.trim();
    if (this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI) {
      this.changeScreenName(newName);
    } else {
      if (
        ScreenDesignHelper.isDiFriendlyNameUnique(
          this.props.sd.getScreenDesign().displayItems,
          this.props.sd.selectedKuikaIds[0],
          newName
        ) === false
      ) {
        KMainFunctions.displayWarningNotification("This name is not unique. Please retry with another name.");
        this.setState({ isRenaming: false, renamingId: "" });
        return;
      }
      const nameRules = /^[A-Za-z0-9-_ ]+$/;
      const isNameValid = nameRules.test(newName);
      if (!isNameValid) {
        KMainFunctions.displayWarningNotification(
          "Name is invalid. Name should contain only letters, numbers, spaces, underscores and dashes."
        );
        this.setState({ isRenameLoading: false });
        return;
      }
      this.changeDiFriendlyName(newName);
      this.props.sd.setRenamingDisplayItemId(undefined);
    }
  };

  propertiesHeaderMenu = () => {
    const multiSelectInfo = this.getMultiSelectInfo();
    const isMultiSelect = multiSelectInfo?.isMultiSelect || false;

    if (isMultiSelect) {
      const menu: any = [
        {
          label: "Copy",
          icon: <KuikaSvg width={16} height={16} name="copy" />,
          onClick: () => this.copyMultiSelectedItems()
        },
        {
          label: "Duplicate",
          icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
          onClick: () => this.duplicateMultiSelectedItems()
        },
        {
          label: <KuikaText>Delete</KuikaText>,
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: () => this.deleteMultiSelectedItems(),
          danger: true
        }
      ].filter(Boolean);

      return menu;
    }

    const selectedDisplayItem = this.getSelectedDisplayItem();
    const isMicroApp = this.isMicroApp(selectedDisplayItem);
    const selectedDIParent = selectedDisplayItem?.parentId
      ? this.props.sd.screenDesignDictionary[selectedDisplayItem?.parentId]
      : undefined;
    const isEditableTableColumnChild = selectedDIParent?.item?.name === "EditableTableColumn";
    const isChildOfFormComponent: boolean =
      !!selectedDisplayItem?.id &&
      ScreenDesignHelper.getIsChildOfForm(selectedDisplayItem?.id, this.props.sd.screenDesignDictionary);
    const isInteractionDisabled =
      !!selectedDisplayItem?.id &&
      ScreenDesignHelper.getIsInteractionDisabled(selectedDisplayItem?.id, this.props.sd.screenDesignDictionary);
    const isParentsChildrenInteractionDisabled =
      !!selectedDisplayItem?.id &&
      ScreenDesignHelper.getIsParentsChildrenInteractionDisabled(
        selectedDisplayItem?.id,
        this.props.sd.screenDesignDictionary
      );

    const clipboardValue = this.props.sd.getClipboardValue();

    const menu: any = [
      {
        label: "Rename",
        icon: <KuikaSvg width={16} height={16} name="edit" />,
        disabled: this.props.sd.renamingDisplayItemId === selectedDisplayItem?.id,
        onClick: (e) => {
          if (this.props.sd.renamingDisplayItemId === selectedDisplayItem?.id) {
            e.preventDefault();
            return;
          }
          this.renameSelectedItem();
        }
      },
      this.getSelectedDisplayItem()?.id !== SpecialDIIds.rootDI &&
        !isEditableTableColumnChild && {
          label: "Copy",
          icon: <KuikaSvg width={16} height={16} name="copy" />,
          hidden: isMicroApp,
          onClick: () => this.copySelectedItem()
        },
      this.getSelectedDisplayItem()?.id !== SpecialDIIds.rootDI &&
        !isEditableTableColumnChild &&
        clipboardValue &&
        !isMicroApp &&
        !isEditableTableColumnChild &&
        !isChildOfFormComponent &&
        !isInteractionDisabled &&
        !isParentsChildrenInteractionDisabled && {
          label: "Paste",
          icon: <KuikaSvg width={16} height={16} name="clipboard" />,
          onClick: () => this.pasteSelectedItem()
        },
      this.getSelectedDisplayItem()?.id !== SpecialDIIds.rootDI &&
        !isEditableTableColumnChild &&
        clipboardValue &&
        !isMicroApp && {
          label: "Paste Styling",
          icon: <KuikaSvg width={16} height={16} name="brush" />,
          onClick: () => this.pasteStylingSelectedItem()
        },
      this.getSelectedDisplayItem()?.id !== SpecialDIIds.rootDI &&
        !isEditableTableColumnChild &&
        this.getSelectedDisplayItem()?.id !== SpecialDIIds.headerDI &&
        this.getSelectedDisplayItem()?.id !== SpecialDIIds.footerDI &&
        this.getSelectedDisplayItem()?.id !== SpecialDIIds.pageHeaderDI &&
        this.getSelectedDisplayItem()?.id !== SpecialDIIds.pageFooterDI &&
        !isMicroApp &&
        !isEditableTableColumnChild &&
        !isChildOfFormComponent &&
        !isInteractionDisabled &&
        !isParentsChildrenInteractionDisabled && {
          label: "Duplicate",
          icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
          onClick: () => this.cloneSelectedItem()
        },
      this.getSelectedDisplayItem()?.id !== SpecialDIIds.rootDI &&
        !isEditableTableColumnChild &&
        !isChildOfFormComponent &&
        !isInteractionDisabled &&
        !isParentsChildrenInteractionDisabled && {
          label: <KuikaText>Delete</KuikaText>,
          icon: <KuikaSvg width={16} height={16} name="trash" />,
          onClick: () => this.deleteSelectedItem(),
          danger: true
        },
      this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI && {
        label: "Duplicate",
        icon: <KuikaSvg width={16} height={16} name="copy-plus" />,
        onClick: () => this.duplicateScreen()
      },
      this.getSelectedDisplayItem()?.id === SpecialDIIds.rootDI && {
        label: <KuikaText>Delete</KuikaText>,
        icon: <KuikaSvg width={16} height={16} name="trash" />,
        onClick: (e) => this.triggerDeleteScreenModal(e),
        danger: true
      }
    ].filter(Boolean);

    return menu;
  };

  isMicroApp = (node: any): boolean => {
    const item: IDisplayItemComponent | undefined = node?.item;
    const itemDefinition = this.props.sd.getComponentDefinition(item);
    if (itemDefinition && itemDefinition.name === "MicroApp") {
      return true;
    }
    return false;
  };

  copySelectedItem = () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.copyItemMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  pasteSelectedItem = () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.pasteItemMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  pasteStylingSelectedItem = () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.pasteItemStyleMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  cloneSelectedItem = () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];
    const message = {
      kuikaid: sourceKuikaId.toString()
    };
    this.props.sd.cloneItemMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  deleteSelectedItem = async () => {
    const sourceKuikaId = this.props.sd.selectedKuikaIds[0];

    const interactionDisabledFriendlyNames: string[] =
      ScreenDesignHelper.getFriendlyNamesOfChildDisplayItemsWhichInteractionsDisabled(
        parseInt(sourceKuikaId, 10),
        this.props.sd.screenDesignDictionary
      );

    if (interactionDisabledFriendlyNames.length > 0) {
      KMainFunctions.displayWarningNotification(
        `This element can not be deleted as it contains some elements with disabled interactions. Move these elements to another parent element and try again: ${interactionDisabledFriendlyNames.join(
          ", "
        )}`
      );
      return;
    }

    const message = { kuikaid: sourceKuikaId.toString() };

    this.props.sd.removeItemMessage(message);
    this.setState({ dropdownVisible: false });
    this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
  };

  duplicateScreen = async () => {
    const accessName = "Screens";
    if (
      KMainFunctions.isDomainHasAccess(
        accessName,
        this.props.sd.screenList.filter((screen) => screen.publicMetaData?.screenType !== EScreenType.Report).length
      ) === false
    ) {
      // Handle domain access required
      return;
    }

    this.props.sd.processWithSpinner(async () => {
      try {
        this.setState({ dropdownVisible: false });
        const result = await duplicateDesignerScreen(getSdInstance(), this.props.sd.sdId);
        if ("error" in result) {
          throw new Error(result.error);
        }

        if (result.success) {
          KMainFunctions.displaySuccessNotification("Duplicate has been successful.");
          await this.props?.sd?.setIsScreenWindowReload(true);
        }
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
      }
    });
  };

  getMultiSelectInfo = (): { isMultiSelect: boolean; count: number } | null => {
    try {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (!iframe || !iframe.contentWindow) {
        return null;
      }

      const iframeWindow = iframe.contentWindow as any;
      const multiSelectManager = iframeWindow?.kuika?.reactHelper?.multiSelectManager;

      if (!multiSelectManager) {
        return null;
      }

      const hasSelection = multiSelectManager.hasSelection();
      const count = multiSelectManager.getSelectedCount ? multiSelectManager.getSelectedCount() : 0;

      return {
        isMultiSelect: hasSelection && count > 1,
        count
      };
    } catch (error) {
      return null;
    }
  };

  copyMultiSelectedItems = () => {
    try {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (!iframe || !iframe.contentWindow) return;

      const iframeWindow = iframe.contentWindow as any;
      const reactHelper = iframeWindow?.kuika?.reactHelper;

      if (reactHelper && reactHelper.copyMultiSelectedItems) {
        reactHelper.copyMultiSelectedItems();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  duplicateMultiSelectedItems = () => {
    try {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (!iframe || !iframe.contentWindow) return;

      const iframeWindow = iframe.contentWindow as any;
      const reactHelper = iframeWindow?.kuika?.reactHelper;

      if (reactHelper && reactHelper.cloneMultiSelectedItems) {
        reactHelper.cloneMultiSelectedItems();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  deleteMultiSelectedItems = () => {
    try {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;
      if (!iframe || !iframe.contentWindow) return;

      const iframeWindow = iframe.contentWindow as any;
      const reactHelper = iframeWindow?.kuika?.reactHelper;

      if (reactHelper && reactHelper.removeMultiSelectedItems) {
        reactHelper.removeMultiSelectedItems();
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  triggerDeleteScreenModal = (e) => {
    this.setState({ dropdownVisible: false });

    if (this.props.sd.screenList.length === 1) {
      KMainFunctions.displayWarningNotification("You can't delete last screen of project.");
      return;
    }

    this.setState({ triggerScreenDeleteModal: true });
  };

  deleteScreen = async () => {
    const designSituation = this.props.sd.dashboardState === DashboardState.design ? "screen" : "report";
    const reportDesignPageToOpen = this.props.sd.screenList?.find(
      (sd) => sd.publicMetaData?.screenType === EScreenType.Report && sd.id !== this.props.sd.sdId
    );
    const screenToOpen = this.props.sd.screenList?.find(
      (sd) => sd.id !== this.props.sd.sdId && sd.publicMetaData?.screenType !== EScreenType.Report
    );
    if (designSituation === "screen" && !screenToOpen) {
      KMainFunctions.displayWarningNotification("You can't delete last screen of project.");
      return;
    }
    try {
      await this.props.sd.saveScreen(true);
      const res = await ScreenService.DeleteCurrentScreen(this.props.sd.sdId.toString());
      if (res.status === 200) {
        KMainFunctions.displaySuccessNotification(
          `${this.props.sd.sdName} ${designSituation} has been deleted successfully.`
        );
        this.props?.sd?.setIsScreenWindowReload(true);
        if (designSituation === "report") {
          if (reportDesignPageToOpen) {
            this.props.sd.loadScreen(reportDesignPageToOpen.id);
          } else {
            this.props.sd.loadScreen(screenToOpen?.id);
            this.props.sd.setVisibilityOfCreateReportDesignPage(true);
            this.props.sd.setDashboardState(DashboardState.design);
          }
        } else {
          this.props.sd.loadScreen(screenToOpen?.id);
        }
      }
    } catch (error) {
      KMainFunctions.exceptionHandler(error);
    }
  };

  componentMenu = () => {
    const hasUserAccessCreateComponent = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.CreateComponent,
      this.props.sd.project?.projectAccess
    );

    return (
      <>
        {hasUserAccessCreateComponent && (
          <KuikaButton
            onClick={(e) => e.stopPropagation()}
            isIcon
            type="tertiary"
            color="neutral"
            data-onboarding-id="right-panel-properties"
            data-testid="rightPanelHeaderThreeDotButton"
          >
            <KuikaDropdownMenu options={this.propertiesHeaderMenu() as any}>
              <KuikaSvg width={16} height={16} name="dots" />
            </KuikaDropdownMenu>
          </KuikaButton>
        )}
      </>
    );
  };

  render() {
    const multiSelectInfo = this.getMultiSelectInfo();
    const isMultiSelect = multiSelectInfo?.isMultiSelect || false;
    const multiSelectCount = multiSelectInfo?.count || 0;

    return (
      <>
        <div
          className="kuika_right-panel-header"
          style={{
            borderLeft: this.props.sd.isRightPanelCollapsed[this.props.sd.dashboardState]
              ? "1px solid var(--dark-3)"
              : "none",
            // KOA-18354: the panel now sits BESIDE row-2 rather than under it, so this strip is
            // the right-hand neighbour of the sub-bar and its bottom hairline has to land on the
            // same line. `--subbar-height` is defined only while that strip exists
            // (`.kuika-shell--has-subbar`), so the 48px fallback keeps every other view exactly
            // as it was. Same trick as the left panel's first-strip rule in design-left-panel.scss.
            height: this.props.sd.isRightPanelCollapsed[this.props.sd.dashboardState]
              ? "40px"
              : "var(--subbar-height, 48px)"
          }}
        >
          <div className="kuika_right-panel-header__title">
            <KuikaTooltip
              content={
                isMultiSelect
                  ? `${multiSelectCount} elements selected`
                  : KMainFunctions.isUserKuikaDeveloper(this.props?.context.userInfo?.username)
                    ? this.props.sd.selectedKuikaIds.length === 1 && Number(this.props.sd.selectedKuikaIds[0]) > 0
                      ? this.props.sd.selectedKuikaIds[0]
                      : ""
                    : this.getItemName(true)
              }
            >
              <KuikaMenuItem
                title={
                  isMultiSelect ? (
                    `${multiSelectCount} elements selected`
                  ) : this.state.isRenaming ? (
                    <KuikaInput
                      data-testid="rightPanelHeaderRenameInput"
                      defaultValue={this.getItemName(false)}
                      onKeyDown={this.handleNameKeypress}
                      onBlur={this.handleNameOnBlur}
                      disabled={this.state.isRenameLoading}
                      autoFocus
                    />
                  ) : (
                    this.getItemName(true)
                  )
                }
                leftIcon={
                  isMultiSelect ? (
                    <KuikaSvg width={16} height={16} name="list-check" />
                  ) : (
                    this.getComponentIconImgByIconName()
                  )
                }
                rightUnit={this.componentMenu()}
                // A title strip, not a list row: nothing happens on click, so it must not light up on hover.
                interactive={false}
              />
            </KuikaTooltip>
          </div>
        </div>
        <DeleteModal
          itemName={this.props.sd.sdName}
          triggerModal={this.state.triggerScreenDeleteModal}
          deleteItem={() => this.deleteScreen()}
          deleteType={this.props.sd.dashboardState === DashboardState.design ? "Screen" : "Report"}
          closeModal={() => this.setState({ triggerScreenDeleteModal: false })}
        />
      </>
    );
  }
}

export default withContext<{}>(withSD<{}>(RightPanelHeader));
